'use client';

import { useRef, useEffect, useState, useCallback, ChangeEvent, FC, KeyboardEvent } from 'react';
import { useTranslations } from 'next-intl';
import { useUnit } from 'effector-react';
import { Button, ButtonType } from '@nx-frontend/shared/ui/button';
import { UiInput, UiInputSize, UiInputView } from '@nx-frontend/shared/ui/input';
import { $ModalStore, closeModalEvent } from '@nx-frontend/shared/ui/modal';
import { PopupPayload, PopupType, RenamePopupPayload } from '@nx-frontend/shared/types';

const getRenamePopupPayload = (payload: PopupPayload | undefined): RenamePopupPayload | null => {
  if (payload && payload.payloadType === PopupType.RENAME) {
    return payload;
  }
  return null;
};

export const RenameNodePopup: FC = () => {
  const t = useTranslations('webnotes');
  const [store, closeModal] = useUnit([$ModalStore, closeModalEvent]);

  const modal = store.find((modal) => modal.payload && modal.payload.payloadType === PopupType.RENAME);
  const payload = getRenamePopupPayload(modal?.payload);

  const inputRef = useRef<HTMLInputElement>(null);
  const [value, setValue] = useState<string>(payload?.value.trim() || '');
  const [error, setError] = useState<boolean>(false);

  const onChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      if (error && event.target.value.trim().length) {
        setError(false);
      }
      if (event.target.value.length > 50) {
        event.preventDefault();
      } else {
        setValue(event.target.value);
      }
    },
    [error]
  );

  const onSaveClick = useCallback(() => {
    if (inputRef.current) {
      if (!inputRef.current.value.trim().length) {
        setError(true);
      } else {
        typeof payload?.onApply === 'function' && payload.onApply(inputRef.current.value.trim());
        closeModal();
      }
    }
  }, [payload, inputRef, closeModal]);

  const onKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter') {
        onSaveClick();
      }
    },
    [onSaveClick]
  );

  useEffect(() => {
    if (inputRef && inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  if (!payload) return null;
  const type = payload.type;

  let label = t('webnotes-page_name');
  let errorText = t('webnotes-page_name_is_required');
  let placeholder = t('webnotes-enter_page_name');
  const typePossibleValues = ['folder', 'file', 'workspace', 'tag', 'agent']; // type also can be 'note'
  if (typePossibleValues.find((el) => el === type)) {
    label = t(`webnotes-${type}_name`);
    errorText = t(`webnotes-${type}_name_is_required`);
    placeholder = t(`webnotes-enter_${type}_name`);
  }

  return (
    <>
      <div className="min-w-64 mb-5">
        <UiInput
          id={`rename-${payload.type}-input`}
          type="text"
          label={label}
          value={value}
          name={`rename-${payload.type}-input`}
          placeholder={placeholder}
          onChange={onChange}
          onKeyDown={onKeyDown}
          view={UiInputView.LABEL_TOP}
          inputRef={inputRef}
          size={UiInputSize.SMALL}
          error={error}
          errorText={errorText}
          themeColors={payload.themeColors}
          themeStyle={payload.themeStyle}
        />
      </div>
      {payload.message && (
        <div className="px-3 py-4 bg-neutral-50 text-xs text-neutral-500 max-w-64 rounded mb-5 border border-neutral-200">
          {' '}
          {payload.message}
        </div>
      )}
      <div className="rename-popup-buttons-wrapper flex justify-center gap-4">
        <Button
          type={ButtonType.STROKED}
          themeColors={payload.themeColors}
          themeStyle={payload.themeStyle}
          onClick={closeModal}
        >
          {t('webnotes-cancel')}
        </Button>
        <Button
          type={ButtonType.PRIMARY}
          themeColors={payload.themeColors}
          themeStyle={payload.themeStyle}
          onClick={onSaveClick}
        >
          {t('webnotes-save')}
        </Button>
      </div>
    </>
  );
};
